import { useEffect, useState, useRef, useCallback } from 'react';
import { getRandomInsights } from '../services/supabase/content';
import type { FeaturedInsight } from '../services/supabase/content';
import { InsightCard } from './insight/InsightCard';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';
import type { CarouselApi } from './ui/carousel';

interface FeaturedInsightsCarouselProps {
  className?: string;
}

// Global cache to prevent multiple requests across component instances
// Cache is now quality-aware (only caches high-quality insights)
let featuredInsightsCache: FeaturedInsight[] | null = null;
let isGloballyFetching = false;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
const globalFetchPromise: { current: Promise<FeaturedInsight[]> | null } = { current: null };

export function FeaturedInsightsCarousel({ className }: FeaturedInsightsCarouselProps) {
  const [featuredInsights, setFeaturedInsights] = useState<FeaturedInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const autoRotationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Add diagnostic logging for state changes
  useEffect(() => {

  }, [isLoading, featuredInsights.length, error]);

  // Function to check if screen is small
  const checkScreenSize = useCallback(() => {
    setIsSmallScreen(window.innerWidth < 768); // md breakpoint in Tailwind
  }, []);

  // Set up auto-rotation for small screens
  useEffect(() => {
    // Initial check
    checkScreenSize();

    // Add event listener for resize
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      // Clear any existing interval when component unmounts
      if (autoRotationIntervalRef.current) {
        clearInterval(autoRotationIntervalRef.current);
      }
    };
  }, [checkScreenSize]);

  // Set up auto-rotation when api is available and screen size changes
  useEffect(() => {
    // Clear any existing interval
    if (autoRotationIntervalRef.current) {
      clearInterval(autoRotationIntervalRef.current);
      autoRotationIntervalRef.current = null;
    }

    // Only set up auto-rotation on small screens
    if (isSmallScreen && api) {
      autoRotationIntervalRef.current = setInterval(() => {
        api.scrollNext();
      }, 5000); // Rotate every 5 seconds
    }
    return () => {
      if (autoRotationIntervalRef.current) {
        clearInterval(autoRotationIntervalRef.current);
      }
    };
  }, [api, isSmallScreen]);

  useEffect(() => {
    const fetchFeaturedInsights = async () => {
      // Create an abort controller for this request
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      
        try {
        // Check if we have valid cached data
        const now = Date.now();
        const isCacheValid = featuredInsightsCache && 
                            featuredInsightsCache.length > 0 && 
                            (now - cacheTimestamp) < CACHE_DURATION;

        if (isCacheValid) {

          if (!signal.aborted) {
            setFeaturedInsights(featuredInsightsCache!);
            setIsLoading(false);
          }
          return;
        }

        // If another instance is already fetching, wait for it
        if (isGloballyFetching && globalFetchPromise.current) {
          const response = await globalFetchPromise.current;
          if (!signal.aborted) {
            setFeaturedInsights(response);
            setIsLoading(false);
          }
          return;
        }

        // Start a new fetch
        isGloballyFetching = true;

        if (!signal.aborted) {
          setIsLoading(true);
          setError(null);
        } else {
          return;
        }

        // Create and store the fetch promise
        globalFetchPromise.current = getRandomInsights(4);
        const response = await globalFetchPromise.current;
        
        // Cache the results with timestamp
        featuredInsightsCache = response;
        cacheTimestamp = now;

        if (!signal.aborted) {
          setFeaturedInsights(response);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch featured insights:', err);
        if (!signal.aborted) {
          console.log('FeaturedInsightsCarousel: Setting error and loading=false');
          setError('Failed to load featured insights');
          setIsLoading(false);
        } else {
          console.log('FeaturedInsightsCarousel: Request aborted, skipping error update');
        }
      } finally {
        isGloballyFetching = false;
        globalFetchPromise.current = null;
      }
    };

    fetchFeaturedInsights();

    // Cleanup function to abort the request if component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="w-full py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return null; // Don't show anything if there's an error
  }

  if (featuredInsights.length === 0) {
    return null; // Don't show anything if there are no insights
  }

  return (
    <div className={`w-full max-w-[1400px] mx-auto px-4 ${className}`}>
      <h2 className="text-[2.75rem] font-averia-bold text-center mb-8">Featured Insights</h2>
      <Carousel
        opts={{
          align: 'start',
          loop: true,
        }}
        setApi={setApi}
        className="w-full"
      >
        <CarouselContent>
          {featuredInsights.map((insight) => (
            <CarouselItem key={insight.id} className="md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
              <div className="p-1 h-full">
                <InsightCard insight={insight} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-center gap-2 mt-4 mb-0">
          <CarouselPrevious className="static translate-y-0 mx-2" />
          <CarouselNext className="static translate-y-0 mx-2" />
        </div>
      </Carousel>
    </div>
  );
} 
